import { Group, Loader, Stack, Text } from '@mantine/core';
import type {
  ChargeItem,
  Coverage,
  CoverageEligibilityResponse,
  DocumentReference,
  Encounter,
  Invoice,
  RelatedPerson,
} from '@medplum/fhirtypes';
import type { SavedPaymentMethod } from '@/lib/stripeBillingService';
import { CardOnFileSection } from '../../../components/billing';
import type { InsuranceAuthorizationRecord } from '@/lib/billing/insuranceAuthorization';
import type { InsuranceCardDocument } from '@/lib/billing/insuranceCards';
import {
  ClientProfileAuthorizationPanel,
  ClientProfileBillingHistoryPanel,
  ClientProfileInsurancePanel,
  ClientProfileVerificationPanel,
} from '../components/ClientProfileBillingSections';

export interface BillingTabProps {
  patientId?: string;
  patientDisplay?: string;
  paymentMethods: SavedPaymentMethod[];
  coverages: Coverage[];
  eligibilityResponses: CoverageEligibilityResponse[];
  encounters: Encounter[];
  chargeItems: ChargeItem[];
  invoices: Invoice[];
  insuranceCards: InsuranceCardDocument[];
  authorizations: InsuranceAuthorizationRecord[];
  statements: DocumentReference[];
  relatedPersons?: RelatedPerson[];
  loading?: boolean;
  onRefresh: () => Promise<void>;
}

export function BillingTab({
  patientId,
  patientDisplay,
  paymentMethods,
  coverages,
  eligibilityResponses,
  encounters,
  chargeItems,
  invoices,
  insuranceCards,
  authorizations,
  statements,
  relatedPersons,
  loading,
  onRefresh,
}: BillingTabProps): JSX.Element {
  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
        <Text size="sm" c="gray.6">Loading billing data…</Text>
      </Group>
    );
  }

  return (
    <Stack gap="lg">
      <CardOnFileSection paymentMethods={paymentMethods} />

      <ClientProfileInsurancePanel
        patientId={patientId}
        patientDisplay={patientDisplay}
        coverages={coverages}
        relatedPersons={relatedPersons}
        insuranceCards={insuranceCards}
        onSaved={onRefresh}
      />

      <ClientProfileVerificationPanel
        coverages={coverages}
        eligibilityResponses={eligibilityResponses}
        onSaved={onRefresh}
      />

      <ClientProfileAuthorizationPanel
        patientId={patientId}
        coverages={coverages}
        authorizations={authorizations}
        onSaved={onRefresh}
      />

      <ClientProfileBillingHistoryPanel
        encounters={encounters}
        chargeItems={chargeItems}
        invoices={invoices}
      />

      {statements.length > 0 && (
        <Text size="sm" c="gray.6">
          {statements.length} billing statement{statements.length === 1 ? '' : 's'} on file.
        </Text>
      )}
    </Stack>
  );
}
